import { Fragment } from 'react';
import { makeStyles, Button, StatusLabel, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, IconButton } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import {
    spacing10,
    spacing20,
    spacing30,
    spacing40,
    fontWeightBold,
    fontSizeLarge,
    fontSizeSmall,
    colorBrandNeutral100,
    colorBackgroundAlertWarning,
    colorTextAlertWarning,
    borderRadiusMedium,
    colorBackgroundAlertNeutral,
    colorTextAlertNeutral,
    colorTextError,
    colorTextNeutral600,
    durationMedium
} from '@ellucian/react-design-system/core/styles/tokens';
import { getGroupStatus, getLatestUploadDate, getLatestReviewerComment, getLatestApplicantComment, shouldShowReviewerMessage, shouldShowApplicantMessage } from '../../../utilities/fileHelpers';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@ellucian/ds-icons/lib';
import FileCard from './FileCard';
import ApplicantMessageInput from './ApplicantMessageInput';
import MessageHistory from './MessageHistory';

const useStyles = makeStyles({
    name: 'DocumentGroupTable'
})(() => ({
    tableHeaderCell: {
        fontWeight: fontWeightBold,
        fontSize: fontSizeLarge },
    expandableRow: {
        cursor: 'default' },
    nonExpandableRow: {
        cursor: 'default' },
    expandedContentRow: {
        backgroundColor: colorBrandNeutral100 },
    collapseContent: {
        maxHeight: 0,
        overflow: 'hidden',
        transition: `max-height ${durationMedium} cubic-bezier(0.4, 0, 0.2, 1)` },
    collapseContentExpanded: {
        maxHeight: '5000px' },
    expandedContent: {
        display: 'flex',
        padding: 'inherit',
        paddingBottom: spacing40 },
    fileList: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing20,
        width: '100%' },
    itemNameText: {
        fontWeight: fontWeightBold,
        textTransform: 'uppercase' },
    emptyStateText: {
        fontStyle: 'italic',
        color: colorTextNeutral600 },
    emptyStateContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing20 },
    infoIcon: {
        color: colorTextNeutral600,
        cursor: 'pointer' },
    documentCountText: {
        color: colorTextNeutral600 },
    documentLabel: {
        fontWeight: fontWeightBold,
        marginBottom: spacing20,
        fontSize: fontSizeSmall },
    uploadButtonContainer: {
        marginTop: spacing20,
        display: 'flex',
        gap: spacing20,
        alignItems: 'center',
        justifyContent: 'flex-start' },
    uploadButton: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing20
    },
    maxFilesAlert: {
        marginBottom: spacing40,
        display: 'flex',
        alignItems: 'center',
        gap: spacing20
    },
    maxFilesAlertIcon: {
        color: colorTextError,
        flexShrink: 0
    },
    maxFilesAlertText: {
        color: colorTextError
    },
    reviewerMessageBox: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing20,
        padding: spacing30,
        backgroundColor: colorBackgroundAlertWarning,
        borderRadius: borderRadiusMedium,
        marginTop: spacing20 },
    reviewerMessageIcon: {
        color: colorTextAlertWarning,
        flexShrink: 0,
        fontSize: fontSizeSmall },
    reviewerMessageLabel: {
        fontWeight: fontWeightBold },
    applicantMessageBox: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing20,
        padding: spacing30,
        backgroundColor: colorBackgroundAlertNeutral,
        borderRadius: borderRadiusMedium,
        marginTop: spacing20 },
    applicantMessageIcon: {
        color: colorTextAlertNeutral,
        flexShrink: 0,
        fontSize: fontSizeSmall,
        marginTop: spacing10 },
    applicantMessageLabel: {
        fontWeight: fontWeightBold,
        color: colorTextNeutral600 },
    hiddenRow: {
        display: 'none' } }));

const DocumentGroupTable = ({ groups, expandedRows, onRowClick, onDeleteFile, onViewFile, onUploadFile, onResubmitQuestion, onSendMessage, locale, hasChanges = false }) => {
    const { classes } = useStyles();
    const intl = useIntl();

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell><Typography className={classes.tableHeaderCell}><FormattedMessage id="supportingDocumentsItemName" /></Typography></TableCell>
                    <TableCell><Typography className={classes.tableHeaderCell}><FormattedMessage id="supportingDocumentsFileCount" /></Typography></TableCell>
                    <TableCell><Typography className={classes.tableHeaderCell}><FormattedMessage id="supplementalItemListStatus" /></Typography></TableCell>
                    <TableCell><Typography className={classes.tableHeaderCell}><FormattedMessage id="supportingDocumentsUploadDate" /></Typography></TableCell>
                    <TableCell><Typography className={classes.tableHeaderCell}><FormattedMessage id="expand" /></Typography></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {groups.map((group, index) => {
                    const isExpanded = expandedRows[index];
                    const status = getGroupStatus(group.files, intl, group.documentReviewStatus);
                    const latestDate = getLatestUploadDate(group.files, locale);
                    const hasFiles = group.files.length > 0;
                    const isNotReceived = status.type === 'default';
                    const accessibleLabel = `${group.label} ${status.text}`;
                    const contentId = `document-table-content-${index}`;
                    
                    // Count only successfully uploaded files (exclude deleted and error states)
                    const activeFiles = group.files.filter(f => f?.status !== 'deleted' && f?.status !== 'error');
                    const activeFileCount = activeFiles.length;
                    const maxFiles = group.fileNumberLimit || 5;
                    
                    // Check if any file in this group is currently uploading
                    const hasUploadingFiles = group.files.some(f => f?.status === 'uploading');
                    
                    // Check if max files limit is reached (independent of uploading status)
                    const hasReachedMaxFiles = activeFileCount >= maxFiles;
                    
                    const isUploadDisabled = hasReachedMaxFiles || hasUploadingFiles;
                    
                    return (
                        <Fragment key={index}>
                            <TableRow 
                                className={hasFiles ? classes.expandableRow : classes.nonExpandableRow}
                                aria-label={accessibleLabel}
                            >
                                <TableCell>
                                    <Typography className={classes.itemNameText}>
                                        {group.label}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {!hasFiles ? (
                                        <div className={classes.emptyStateContainer}>
                                            <Typography className={classes.emptyStateText}>
                                                <FormattedMessage id="supportingDocumentsNoDocumentUploaded" defaultMessage="No document uploaded" />
                                            </Typography>
                                            {isNotReceived && (
                                                <Tooltip title={intl.formatMessage({ id: 'supportingDocumentsNotReceivedHelper' })}>
                                                    <Icon name="info" className={classes.infoIcon} aria-hidden="true" />
                                                </Tooltip>
                                            )}
                                        </div>
                                    ) : (
                                        <Typography>
                                            {group.files.length} <span className={classes.documentCountText}>{group.files.length === 1 ? 'document' : 'documents'}</span>
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <StatusLabel text={status.text} type={status.type} />
                                </TableCell>
                                <TableCell>
                                    <Typography>{latestDate}</Typography>
                                </TableCell>
                                {(hasFiles || group.showUploadButton) && (
                                    <TableCell>
                                        <IconButton 
                                            color="gray" 
                                            onClick={() => onRowClick(index)} 
                                            aria-label={isExpanded ? intl.formatMessage({ id: 'collapse' }) : intl.formatMessage({ id: 'expand' })}
                                            aria-expanded={isExpanded}
                                            aria-controls={contentId}
                                        >
                                            <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} />
                                        </IconButton>
                                    </TableCell>
                                )}
                                {!hasFiles && !group.showUploadButton && (
                                    <TableCell />
                                )}
                            </TableRow>
                            {(hasFiles || group.showUploadButton) && (
                                <TableRow className={!isExpanded ? classes.hiddenRow : undefined} aria-hidden={!isExpanded}>
                                    <TableCell className={classes.tableCell} colSpan={5}>
                                        <div 
                                            id={contentId}
                                            className={`${classes.collapseContent} ${isExpanded ? classes.collapseContentExpanded : ''} ${classes.expandedContent}`}
                                            role="region"
                                            aria-label={intl.formatMessage({ id: 'supportingDocumentsUploadedDocuments' })}
                                        >
                                            <div className={classes.fileList}>
                                                {hasFiles && (
                                                    <>
                                                        <Typography className={classes.documentLabel}>
                                                            <FormattedMessage id="supportingDocumentsUploadedDocuments" />
                                                        </Typography>
                                                        <div role="list">   
                                                            {group.files.map(item => {
                                                                // const isFileUploading = item.status === 'uploading';
                                                                const canDelete = onDeleteFile && group.documentReviewStatus === 'clarificationNeeded' && !hasUploadingFiles;
                                                                const canView = onViewFile && group.documentReviewStatus === 'clarificationNeeded' && !hasUploadingFiles;
                                                                return (
                                                                    <FileCard 
                                                                        key={item.id || item.documentId} 
                                                                        file={item} 
                                                                        locale={locale}
                                                                        isGroupUploading={hasUploadingFiles}
                                                                        onDelete={canDelete ? () => onDeleteFile(item, group.questionId) : undefined}
                                                                        onView={canView ? () => onViewFile(item, group.questionId) : undefined}
                                                                    />
                                                                );
                                                            })}
                                                        </div>
                                                    </>
                                                )}
                                                {hasReachedMaxFiles && group.documentReviewStatus === 'clarificationNeeded' && (
                                                    <div 
                                                        className={classes.maxFilesAlert}
                                                        role="alert"
                                                        aria-live="polite"
                                                    >
                                                        <Icon name="info" className={classes.maxFilesAlertIcon} aria-hidden="true" />
                                                        <Typography variant="body2" className={classes.maxFilesAlertText}>
                                                            <FormattedMessage 
                                                                id="uploadQuestionMaxFilesMessage" 
                                                                values={{ activeCount: activeFileCount, maxCount: maxFiles }}
                                                            />
                                                        </Typography>
                                                    </div>
                                                )}
                                                {(onUploadFile || onResubmitQuestion) && (group.showUploadButton || group.documentReviewStatus === 'clarificationNeeded') && (
                                                    <div className={classes.uploadButtonContainer}>
                                                        {onUploadFile && group.showUploadButton && (
                                                            <span>
                                                                <Button 
                                                                    onClick={() => {
                                                                        onUploadFile(group.questionId);
                                                                    }}
                                                                    color="secondary"
                                                                    size="small"
                                                                    className={classes.uploadButton}
                                                                    startIcon={<Icon name="upload" />}
                                                                    aria-label={intl.formatMessage({ id: 'uploadDocument' })}
                                                                    disabled={isUploadDisabled}
                                                                >
                                                                    <FormattedMessage id="uploadDocument"/>
                                                                </Button>
                                                            </span>
                                                        )}
                                                        {group.documentReviewStatus === 'clarificationNeeded' && onResubmitQuestion && (
                                                            <Button 
                                                                onClick={() => {
                                                                    onResubmitQuestion(group.questionId);
                                                                }}
                                                                color="secondary"
                                                                size="small"
                                                                className={classes.uploadButton}
                                                                startIcon={<Icon name="send" />}
                                                                aria-label={(() => {
                                                                    const baseLabel = intl.formatMessage({ id: 'resubmitQuestion' });
                                                                    if (hasUploadingFiles) {
                                                                        return `${baseLabel} - ${intl.formatMessage({ id: 'uploadDialogStatusUploading' })}`;
                                                                    }
                                                                    return baseLabel;
                                                                })()}
                                                                disabled={hasUploadingFiles || !hasChanges}
                                                            >
                                                                <FormattedMessage id="resubmitQuestion" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                                {shouldShowReviewerMessage(group.documentReviewStatus, group.documentReviewComments) && (
                                                    <div className={classes.reviewerMessageBox}>
                                                        <Icon name="warning" className={classes.reviewerMessageIcon} />
                                                        <Typography variant="body2">
                                                            <span className={classes.reviewerMessageLabel}>
                                                                <FormattedMessage id="supportingDocumentsReviewerMessage" />
                                                            </span>{' '}
                                                            {getLatestReviewerComment(group.documentReviewComments)?.comment}
                                                        </Typography>
                                                    </div>
                                                )}
                                                {shouldShowApplicantMessage(group.documentReviewStatus, group.documentReviewComments) && (
                                                    <div className={classes.applicantMessageBox}>
                                                        <Icon name="user" className={classes.applicantMessageIcon} />
                                                        <Typography variant="body2">
                                                            <span className={classes.applicantMessageLabel}>
                                                                <FormattedMessage id="supportingDocumentsYourMessage" />
                                                            </span>{' '}
                                                            {getLatestApplicantComment(group.documentReviewComments)?.comment}
                                                        </Typography>
                                                    </div>
                                                )}
                                                {group.documentReviewStatus === 'clarificationNeeded' && onSendMessage && (
                                                    <ApplicantMessageInput
                                                        questionId={group.questionId}
                                                        onSendMessage={onSendMessage}
                                                    />
                                                )}
                                                {group.documentReviewStatus === 'clarificationNeeded' && group.documentReviewComments && group.documentReviewComments.length > 0 && (
                                                    <MessageHistory
                                                        comments={group.documentReviewComments}
                                                        locale={locale}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </Fragment>
                    );
                })}
            </TableBody>
        </Table>
    );
};

export default DocumentGroupTable;
